import React, { useState, useEffect, useRef } from 'react';
import moment from 'moment';
import Logo from 'assets/images/header/logo3.svg';
import Clock from 'react-live-clock';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { UserCircle, Menu, X } from 'lucide-react';
import ProfileMenu from 'templates/Header/Profile';
import SideMenu from './SideMenu';

export const Header: React.FC = () => {
  const [openProfile, setOpenProfile] = useState(false);
  const [isSideMenuOpen, setIsSideMenuOpen] = useState(false);
  const { auth } = useSelector((state: RootState) => state.auth);

  const profileMenuRef = useRef<HTMLDivElement>(null);
  const sideMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        profileMenuRef.current &&
        !profileMenuRef.current.contains(event.target as Node)
      ) {
        setOpenProfile(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  useEffect(() => {
    const handleClickOutsideMenu = (event: MouseEvent) => {
      if (
        sideMenuRef.current &&
        !sideMenuRef.current.contains(event.target as Node)
      ) {
        setIsSideMenuOpen(false);
      }
    };
    if (isSideMenuOpen) {
      document.addEventListener('mousedown', handleClickOutsideMenu);
    }
    return () =>
      document.removeEventListener('mousedown', handleClickOutsideMenu);
  }, [isSideMenuOpen]);

  const getGreetingMessage = (): string => {
    const formatter = new Intl.DateTimeFormat('en-GB', {
      hour: 'numeric',
      hour12: false,
      timeZone: 'Europe/London',
    });
    const hourString = formatter.format(new Date());
    const hour = Number(hourString);

    if (hour >= 16) return 'Good Evening!';
    if (hour >= 12) return 'Good Afternoon!';
    return 'Good Morning!';
  };
  

  return (
    <div className='fixed w-full z-50'>
      <header className='h-[3.5rem] bg-theme800 border-b border-theme600 px-4 relative flex justify-between items-center'>
        <div className='flex items-center gap-4'>
          <div className='md:hidden'>
            <button
              className='inline-flex items-center justify-center p-2 rounded-md'
              onClick={() => setIsSideMenuOpen(!isSideMenuOpen)}
            >
              {isSideMenuOpen ? (
                <X className='h-6 w-6 text-white' />
              ) : (
                <Menu className='h-6 w-6 text-white' />
              )}
            </button>
          </div>
          <img
            src={Logo}
            alt='logo'
            className='h-12'
          />
        </div>

        <div className='flex items-center gap-4'>
          <div className='hidden md:flex items-center gap-4 text-sm text-white tracking-wider font-medium'>
            <span className='text-[12px] border-r border-theme600 px-4'>
              {moment(new Date()).format('DD MMM YYYY')}
            </span>
            <div className='w-[5rem] flex justify-start'>
              <Clock
                format='HH:mm:ss'
                ticking={true}
                timezone='Europe/London'
              />
            </div>
          </div>

          <div className='hidden md:flex space-x-2 font-display text-xs text-white items-end'>
            <span className='text-[12px]'>{getGreetingMessage()}</span>
            <span>{auth?.user?.firstname}</span>
          </div>

          <div
            className='relative'
            ref={profileMenuRef}
          >
            {auth?.user?.avatar ? (
              <img
                src={auth?.user?.avatar}
                alt='profile'
                className='h-7 w-7 rounded-full cursor-pointer'
                onClick={() => setOpenProfile(!openProfile)}
              />
            ) : (
              <UserCircle
                className='h-7 w-7 text-white hover:text-red-700 cursor-pointer'
                onClick={() => setOpenProfile(!openProfile)}
              />
            )}
            {openProfile && (
              <div className='absolute top-10 right-0 z-50'>
                <ProfileMenu auth={auth} />
              </div>
            )}
          </div>
        </div>

        {isSideMenuOpen && (
          <div
            ref={sideMenuRef}
            className='fixed top-[3.5rem] left-0 w-full z-40 md:hidden'
          >
            <SideMenu closeMenu={() => setIsSideMenuOpen(false)} />
          </div>
        )}
      </header>
    </div>
  );
};
